import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { FormProvider, useForm } from "react-hook-form";
import BackHeader from "@components/header/BackHeader";
import { Name } from "@pages/join/_components/Name";
import { Nickname } from "@pages/join/_components/Nickname";
import { Birthday } from "@pages/join/_components/Birthday";
import { Gender } from "@pages/join/_components/Gender";
import { Country } from "@pages/join/_components/Country";
import { Address } from "@pages/join/_components/Address";
import BasicButton from "@components/button/BasicButton";
import LabelWithInput from "@components/input/LabelWithInput";
import ProfileImageUpload from "./components/ProfileImageUpload";
import { profileEditSchema } from "@libs/zod/zodVaildation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@libs/reactQuery/queryClient";
import useUserInfoStore from "@store/useUserInfoStore";

interface IProfileEditInput {
  name: string;
  nickname: string;
  year: string;
  month: string;
  day: string;
  birth_date: string;
  gender: string;
  country: string;
  postcode: string;
  address: string;
  address_detail: string;
  isNicknameUnique: boolean;
  profile_image: string;
}

const ProfileEditPage: React.FC = () => {
  const { patchProfile } = useApiRequest();
  const { userInfo } = useUserInfoStore();

  const birthYear = userInfo?.birth_date?.substring(0, 4);
  const birthMonth = userInfo?.birth_date?.substring(4, 6);
  const birthDay = userInfo?.birth_date?.substring(6, 8);

  const { mutate: updateProfile } = useMutation({
    mutationFn: (data: any) => patchProfile(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userInfo"] });
    },
    onError: (error) => {
      console.error("수정 실패:", error);
    },
  });

  const methods = useForm<IProfileEditInput>({
    resolver: zodResolver(profileEditSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,

    defaultValues: {
      name: userInfo?.name || "",
      nickname: userInfo?.nickname || "",
      year: birthYear || "",
      month: birthMonth || "",
      day: birthDay || "",
      gender: userInfo?.gender || "",
      country: userInfo?.country || "",
      postcode: userInfo?.postcode || "",
      address: userInfo?.address || "",
      address_detail: userInfo?.address_detail || "",
      // isNicknameUnique: false,
      isNicknameUnique: userInfo?.nickname ? true : false, // 기존 닉네임이 있다면 true

      profile_image: userInfo?.profile_image || "",
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const {
    handleSubmit,
    formState: { isValid },
    watch,
    setValue,
    reset,
  } = methods;

  console.log(watch("isNicknameUnique"));

  const isFormValid = isValid && watch("isNicknameUnique") === true;

  const onSubmit = async (data: IProfileEditInput) => {
    const submitData = {
      name: data?.name || "",
      nickname: data?.nickname || "",
      gender: data?.gender || "",
      country: data.country || "",
      postcode: data?.postcode || "",
      address: data?.address || "",
      address_detail: data?.address_detail || "",
      profile_image: data.profile_image || userInfo?.profile_image,
      birth_date: `${data.year}${data.month}${data.day}`,
    };

    navigate("/my-page");
    updateProfile(submitData);
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (userInfo) {
      reset({
        name: userInfo.name || "",
        nickname: userInfo.nickname || "",
        year: userInfo.birth_date?.substring(0, 4) || "",
        month: userInfo.birth_date?.substring(4, 6) || "",
        day: userInfo.birth_date?.substring(6, 8) || "",
        gender: userInfo.gender || "",
        country: userInfo.country || "",
        postcode: userInfo.postcode || "",
        address: userInfo.address || "",
        address_detail: userInfo.address_detail || "",
        // isNicknameUnique: false,
        isNicknameUnique: userInfo?.nickname ? true : false, // 기존 닉네임이 있다면 true
        profile_image: userInfo.profile_image || "",
      });
    }
  }, [userInfo]);

  // const handleBirthDateChange = (field: any, value: string) => {
  //   setValue(field, value); // year, month, day 값 업데이트
  //   if (field === "day") {
  //     const birthDate = `${watch("year")}${watch("month")}${value}`;
  //     setValue("birth_date", birthDate); // birth_date 값 자동으로 합치기
  //   }
  // };

  return (
    <>
      <BackHeader title="프로필 편집" />
      <FormProvider {...methods}>
        <form onSubmit={(e) => e.preventDefault()} noValidate>
          <Container>
            {/* 이미지 편집 */}
            <ProfileImageUpload
              initialImage={userInfo?.profile_image}
              onImageChange={(imageUrl) => {
                setValue("profile_image", imageUrl);
              }}
            />
            {/* 이메일 필드 */}
            <LabelWithInput label={"이메일 계정"} required={true}>
              <EmailWrapper>
                <EmailText>{userInfo?.login_id}</EmailText>
              </EmailWrapper>
            </LabelWithInput>

            {/* 이름 입력 필드 */}
            <Name />

            {/* 닉네임 입력 필드 */}
            <Nickname />

            {/* 생년월일 선택 필드 */}
            <Birthday />

            {/* 성별 선택 필드 */}
            <Gender />

            {/* 국가 선택 필드 */}
            <Country />

            {/* 주소 선택 필드  */}
            <Address />

            <BasicButton
              title="수정하기"
              height="5.2rem"
              width="100%"
              padding="1.6rem 1.2rem"
              onClick={() => setIsModalOpen(true)}
              disabled={!isFormValid}
            />

            <BasicModal
              open={isModalOpen}
              setOpen={setIsModalOpen}
              activeName="확인"
              activeEvent={handleModalConfirm}
              padding="2rem">
              <ModalContent mainMessage={"변경한 내용을 저장하시겠습니까?"} />
            </BasicModal>
          </Container>
        </form>
      </FormProvider>
    </>
  );
};

export default ProfileEditPage;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4rem",
  padding: "2rem 2rem 3.4rem 2rem",
});

const EmailWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "1.6rem",
  backgroundColor: theme.color.border_gray,
  height: "5.2rem",
  border: `1px solid ${theme.color.light_gray}`,
}));

const EmailText = styled.span(({ theme }) => ({
  color: theme.color.light_gray,
  fontSize: "1.6rem",
}));
